import { formatMatterMoney, formatMattersMoneySum } from '../lib/matterCurrency.js';
import { isPaidStatus } from '../lib/paidBeforeAssignment.js';
import { DollarIcon, OpenDemandsIcon, PaidIcon } from './kpiIcons.jsx';

function formatCompactMoney(value, currencyOrMatter = 'USD') {
  return formatMatterMoney(value, currencyOrMatter, {
    notation: 'compact',
    maximumFractionDigits: 1,
  });
}

/**
 * Collection KPI strip for a debtor record.
 * Matches demand-record KPI card styling: Open demands, Total owed, Recovered.
 */
export default function DebtorKpiStrip({
  debtor,
  metricsOpen = false,
  onToggleMetrics,
}) {
  if (!debtor) return null;

  const openCount = Number(debtor.openCount) || 0;
  const totalOwed = Number(debtor.totalOwed) || 0;
  const recovered = Number(debtor.recovered) || 0;
  const recoveredPct =
    totalOwed > 0 ? Math.round((recovered / totalOwed) * 100) : 0;
  const matters = Array.isArray(debtor.matters) ? debtor.matters : [];
  const paidMatters = matters.filter((matter) => isPaidStatus(matter.Status));
  const totalOwedLabel = matters.length
    ? formatMattersMoneySum(matters, (matter) => matter.Amount_Owed)
    : formatMatterMoney(totalOwed);
  const recoveredLabel = paidMatters.length
    ? formatMattersMoneySum(paidMatters, (matter) => matter.Amount_Owed)
    : formatMatterMoney(recovered);
  const compactOwed = matters.length
    ? formatMattersMoneySum(matters, (matter) => matter.Amount_Owed, {
        notation: 'compact',
        maximumFractionDigits: 1,
      })
    : formatCompactMoney(totalOwed);
  const compactRecovered = paidMatters.length
    ? formatMattersMoneySum(paidMatters, (matter) => matter.Amount_Owed, {
        notation: 'compact',
        maximumFractionDigits: 1,
      })
    : formatCompactMoney(recovered);

  const summary = [
    `${openCount} open`,
    `${compactOwed} owed`,
    `${compactRecovered} paid`,
  ].join(' · ');

  return (
    <>
      <button
        type="button"
        className="debtor-metrics-summary matter-kpi-summary"
        aria-expanded={metricsOpen}
        onClick={() => onToggleMetrics?.(!metricsOpen)}
      >
        <span>{summary}</span>
        <span className="debtor-metrics-summary-toggle">
          {metricsOpen ? 'Hide' : 'KPIs'}
        </span>
      </button>

      <div
        className={
          metricsOpen
            ? 'metrics-bar matter-kpi-strip cols-3 is-expanded'
            : 'metrics-bar matter-kpi-strip cols-3'
        }
        aria-label="Debtor collection metrics"
      >
        <div className="matter-kpi-card tone-open">
          <span className="matter-kpi-icon" aria-hidden="true">
            <OpenDemandsIcon />
          </span>
          <span className="matter-kpi-label">Open demands</span>
          <strong className="matter-kpi-value">{openCount}</strong>
          <small className="matter-kpi-sub">
            {openCount === 1 ? 'Unpaid demand' : 'Unpaid demands'}
          </small>
        </div>

        <div className="matter-kpi-card tone-owed">
          <span className="matter-kpi-icon" aria-hidden="true">
            <DollarIcon />
          </span>
          <span className="matter-kpi-label">Total owed</span>
          <strong className="matter-kpi-value">{totalOwedLabel}</strong>
          <small className="matter-kpi-sub">Open balance</small>
        </div>

        <div className="matter-kpi-card tone-paid">
          <span className="matter-kpi-icon" aria-hidden="true">
            <PaidIcon />
          </span>
          <span className="matter-kpi-label">Recovered</span>
          <strong className="matter-kpi-value">{recoveredLabel}</strong>
          <small className="matter-kpi-sub">
            {totalOwed > 0 ? `${recoveredPct}% of owed` : '—'}
          </small>
        </div>
      </div>
    </>
  );
}
